import { type Ref, ref, watch } from 'vue'

import {
  type CreatorReportItem,
  type CreatorReportRaw,
  type CreatorReportStatus,
  normalizeCreatorReports,
} from '@/lib/adapters/creator-report.adapter'
import { config } from '@/lib/helpers'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import { useErrorStore } from '@/stores/error'

type CreateCreatorReportResponse = {
  uuid: string
  status: CreatorReportStatus
}

export const getCreatorReportHtmlUrl = (uuid: string): string =>
  `${config.metapicApiUrl}/v2/claude/creator-reports/${uuid}`

export const useGetCreatorReports = (userIdRef: Ref<number>) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const data = ref<CreatorReportItem[]>([])
  const errorStore = useErrorStore()

  const refresh = async () => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.get<{ data?: CreatorReportRaw[] } | CreatorReportRaw[]>(
        `/v2/claude/users/${userIdRef.value}/creator-reports`,
      )
      const list = Array.isArray(response.data) ? response.data : (response.data?.data ?? [])
      data.value = normalizeCreatorReports(list)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch(userIdRef, refresh, { immediate: true })

  return { data, isLoading, error, refresh }
}

export const useCreateCreatorReport = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const createReport = async (
    userId: number,
    query: string,
    numberOfImages?: number,
  ): Promise<boolean> => {
    try {
      isLoading.value = true
      error.value = null
      await request.post<CreateCreatorReportResponse>('/v2/claude/creator-report', {
        user_id: userId,
        query,
        ...(numberOfImages ? { number_of_images: numberOfImages } : {}),
      })
      return true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return false
    } finally {
      isLoading.value = false
    }
  }

  return { createReport, isLoading, error }
}
